const ONES: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
};
const TEENS: Record<string, number> = {
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};
const TENS: Record<string, number> = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fourty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};
const SCALE_VALUES: Record<string, number> = {
  hundred: 100,
  thousand: 1000,
  million: 1000000,
};
const ORDINALS: Record<string, number> = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
  sixth: 6,
  seventh: 7,
  eighth: 8,
  // Unformatted transcripts sometimes spell "eighth" this way.
  eigth: 8,
  ninth: 9,
  tenth: 10,
  eleventh: 11,
  twelfth: 12,
  thirteenth: 13,
  fourteenth: 14,
  fifteenth: 15,
  sixteenth: 16,
  seventeenth: 17,
  eighteenth: 18,
  nineteenth: 19,
  twentieth: 20,
  thirtieth: 30,
};

const has = (table: Record<string, number>, word: string | undefined): word is string =>
  word !== undefined && Object.prototype.hasOwnProperty.call(table, word);

export const getSingleDigitValue = (word: string): number | undefined =>
  has(ONES, word.toLowerCase()) ? ONES[word.toLowerCase()] : undefined;
export const isScaleWord = (word: string): boolean => has(SCALE_VALUES, word);

export const NUMBER_WORD_SOURCE = [
  ...Object.keys(ONES),
  ...Object.keys(TEENS),
  ...Object.keys(TENS),
  ...Object.keys(SCALE_VALUES),
].join("|");
export const SINGLE_DIGIT_WORD_SOURCE = Object.keys(ONES).join("|");
export const ORDINAL_WORD_SOURCE = Object.keys(ORDINALS).join("|");

const DATE_WORD_SOURCE = `${NUMBER_WORD_SOURCE}|oh`;
const DATE_PART_SOURCE = `(?:\\d{1,4}|(?:${DATE_WORD_SOURCE})(?:[\\s-]+(?:and[\\s-]+)?(?:${DATE_WORD_SOURCE}))*)`;
const DATE_SEPARATOR_SOURCE = "\\s*(?:\\/|\\bslash\\b)\\s*";
const DICTATED_DATE_PATTERN = new RegExp(
  `\\b(${DATE_PART_SOURCE})${DATE_SEPARATOR_SOURCE}(${DATE_PART_SOURCE})(?:${DATE_SEPARATOR_SOURCE}(${DATE_PART_SOURCE}))?\\b`,
  "gi"
);
const SPOKEN_NUMBER_RUN_PATTERN = new RegExp(
  `\\b(?:${DATE_WORD_SOURCE})(?:[\\s-]+(?:and[\\s-]+)?(?:${DATE_WORD_SOURCE}))*\\b`,
  "gi"
);

export interface NumberGroup {
  value: number;
  length: number;
}

// Reads one 0-99 group ("nine", "fifteen", "twenty six", "oh five") from the start of `tokens`.
export const readGroup = (tokens: string[]): NumberGroup | null => {
  const [first, second] = tokens;
  if (has(TENS, first)) {
    return has(ONES, second) && ONES[second] > 0
      ? { value: TENS[first] + ONES[second], length: 2 }
      : { value: TENS[first], length: 1 };
  }
  if (has(TEENS, first)) return { value: TEENS[first], length: 1 };
  if (first === "oh") {
    return has(ONES, second) ? { value: ONES[second], length: 2 } : null;
  }
  if (has(ONES, first)) return { value: ONES[first], length: 1 };
  return null;
};

export const readGroups = (tokens: string[]): number[] | null => {
  const groups: number[] = [];
  let index = 0;
  while (index < tokens.length) {
    const group = readGroup(tokens.slice(index));
    if (!group) return null;
    groups.push(group.value);
    index += group.length;
  }
  return groups;
};

const parseBelowThousand = (tokens: string[]): number | null => {
  const hundredIndex = tokens.indexOf("hundred");
  if (hundredIndex === -1) {
    const groups = readGroups(tokens);
    return groups?.length === 1 ? groups[0] : null;
  }

  const hundreds = readGroups(tokens.slice(0, hundredIndex));
  if (!hundreds || hundreds.length !== 1 || hundreds[0] < 1 || hundreds[0] > 9) return null;
  const rest = tokens.slice(hundredIndex + 1);
  const remainder = rest.length === 0 ? 0 : parseBelowThousand(rest);
  return remainder === null || remainder > 99 ? null : hundreds[0] * 100 + remainder;
};

// Parses "two thousand twenty six", "three hundred five", "one million" (tokens without "and").
export const parseCardinal = (tokens: string[]): number | null => {
  for (const scaleWord of ["million", "thousand"]) {
    const scaleIndex = tokens.indexOf(scaleWord);
    if (scaleIndex === -1) continue;

    const before = tokens.slice(0, scaleIndex);
    const after = tokens.slice(scaleIndex + 1);
    const multiplier = before.length === 0 ? 1 : parseCardinal(before);
    const remainder = after.length === 0 ? 0 : parseCardinal(after);
    const scale = SCALE_VALUES[scaleWord];
    if (multiplier === null || remainder === null || multiplier >= 1000 || remainder >= scale) {
      return null;
    }
    return multiplier * scale + remainder;
  }
  return parseBelowThousand(tokens);
};

export const toNumberTokens = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[\s-]+/)
    .filter((token) => token && token !== "and");

// Reads a spoken year: "twenty twenty six" or "nineteen eighty five" (19 and 85 join to 1985, not add).
export const parseSpokenYear = (tokens: string[]): number | null => {
  if (tokens.some(isScaleWord)) {
    const value = parseCardinal(tokens);
    return value !== null && value >= 1000 && value <= 2999 ? value : null;
  }

  const groups = readGroups(tokens);
  if (groups?.length !== 2) return null;
  const [century, yearOfCentury] = groups;
  return century >= 10 && century <= 29 ? century * 100 + yearOfCentury : null;
};

const parseDatePart = (part: string, isYear: boolean): number | null => {
  if (/^\d+$/.test(part)) return Number(part);

  const tokens = toNumberTokens(part);
  const groups = tokens.some(isScaleWord) ? null : readGroups(tokens);
  if (groups?.length === 1) return groups[0];
  return isYear ? parseSpokenYear(tokens) : null;
};

const formatDatePart = (part: string, value: number): string =>
  /^\d+$/.test(part) ? part : String(value);

// Number runs are only read as years or unseparated dates after a word that introduces one,
// so "vision twenty twenty" and "four ten twenty twenty patients" stay as spoken.
const DATE_CUE_BEFORE_PATTERN = /\b(?:on|since|dated|born|seen|in|of|from|until|by|dob)[ \t]+$/i;
// A run at the start of the text checks `textBefore`, the note text already before the cursor.
export const hasDateCue = (text: string, offset: number, textBefore = ""): boolean =>
  DATE_CUE_BEFORE_PATTERN.test(offset === 0 ? `${textBefore} ` : text.slice(0, offset));

const isMonth = (value: number): boolean => value >= 1 && value <= 12;
const isDay = (value: number): boolean => value >= 1 && value <= 31;
export const isModernYear = (value: number): boolean => value >= 1900 && value <= 2099;

export interface SpokenDay {
  value: number;
  isOrdinal: boolean;
}

// Reads a day of the month: "ninth", "twenty ninth", "thirty first", "fifteen", "twenty nine".
export const parseSpokenDay = (tokens: string[]): SpokenDay | null => {
  const lastToken = tokens[tokens.length - 1];
  let value: number | null = null;
  const isOrdinal = has(ORDINALS, lastToken);

  if (isOrdinal) {
    const ordinalValue = ORDINALS[lastToken];
    if (tokens.length === 1) value = ordinalValue;
    else if (tokens.length === 2 && has(TENS, tokens[0]) && ordinalValue <= 9) {
      value = TENS[tokens[0]] + ordinalValue;
    }
  } else {
    const groups = readGroups(tokens);
    value = groups?.length === 1 ? groups[0] : null;
  }

  return value !== null && isDay(value) ? { value, isOrdinal } : null;
};

export interface NormalizeDictatedDatesOptions {
  /** Reads "six twenty nine twenty six" as 6/29/26 after a cue word. */
  allowShortYear?: boolean;
}

// A single-digit year is more likely the next number in a list ("on two three four").
const parseShortYear = (tokens: string[]): number | null => {
  const groups = readGroups(tokens);
  if (groups?.length !== 1) return null;
  return groups[0] >= 10 || tokens[0] === "oh" ? groups[0] : null;
};

// "nine eleven twenty twenty six" -> 9/11/2026. Strict so plain number runs aren't turned into dates.
const parseUnseparatedDate = (run: string, allowShortYear: boolean): string | null => {
  const tokens = toNumberTokens(run);
  const month = readGroup(tokens);
  if (!month || !isMonth(month.value)) return null;
  const day = readGroup(tokens.slice(month.length));
  if (!day || !isDay(day.value)) return null;
  const yearTokens = tokens.slice(month.length + day.length);
  if (yearTokens.length === 0) return null;
  const year = parseSpokenYear(yearTokens);
  if (year !== null && isModernYear(year)) return `${month.value}/${day.value}/${year}`;
  if (!allowShortYear) return null;

  const shortYear = parseShortYear(yearTokens);
  return shortYear === null
    ? null
    : `${month.value}/${day.value}/${String(shortYear).padStart(2, "0")}`;
};

// Turns dictated dates like "nine slash fifteen slash twenty twenty six" into "9/15/2026".
export const normalizeDictatedDates = (
  text: string,
  textBefore = "",
  { allowShortYear = false }: NormalizeDictatedDatesOptions = {}
): string =>
  text
    .replace(DICTATED_DATE_PATTERN, (match, first: string, second: string, third?: string) => {
      const hasYear = third !== undefined;
      const month = parseDatePart(first, false);
      const day = parseDatePart(second, !hasYear);
      const year = hasYear ? parseDatePart(third, true) : null;

      if (month === null || day === null || (hasYear && year === null)) return match;
      if (hasYear && (!isMonth(month) || !isDay(day))) return match;

      const parts = [formatDatePart(first, month), formatDatePart(second, day)];
      if (hasYear && third !== undefined && year !== null) parts.push(formatDatePart(third, year));
      return parts.join("/");
    })
    .replace(SPOKEN_NUMBER_RUN_PATTERN, (run: string, offset: number, whole: string) =>
      hasDateCue(whole, offset, textBefore)
        ? (parseUnseparatedDate(run, allowShortYear) ?? run)
        : run
    );

const TRAILING_DATE_FRAGMENT_PATTERN = new RegExp(
  `(^|\\s)((?:${DATE_PART_SOURCE}${DATE_SEPARATOR_SOURCE})+)$`,
  "i"
);
const LEADING_DATE_PART_PATTERN = new RegExp(`^${DATE_PART_SOURCE}\\b`, "i");

// "two thousand", "twenty" or "nineteen" can be the first half of a year that the next result
// finishes ("two thousand" then "twenty six"). By then both halves are already digits.
const UNFINISHED_YEAR_DATE_PATTERN = /(^|\s)(\d{1,2})\/(\d{1,2})\/(2000|19|20)[ \t]*$/;
const YEAR_REMAINDER_WORD_SOURCE = [
  ...Object.keys(ONES),
  ...Object.keys(TEENS),
  ...Object.keys(TENS),
].join("|");
const LEADING_YEAR_REMAINDER_PATTERN = new RegExp(
  `^(\\d{1,2}|(?:${YEAR_REMAINDER_WORD_SOURCE})(?:[\\s-]+(?:${YEAR_REMAINDER_WORD_SOURCE}))?)(?=[.,;:!?]*\\s*$)`,
  "i"
);

const parseYearRemainder = (text: string): number | null => {
  if (/^\d+$/.test(text)) return Number(text);
  const groups = readGroups(toNumberTokens(text));
  return groups?.length === 1 ? groups[0] : null;
};

const completeYear = (yearStart: string, remainderText: string): number | null => {
  const remainder = parseYearRemainder(remainderText);
  if (remainder === null || remainder < 1 || remainder > 99) return null;
  if (yearStart !== "2000" && remainder < 10) return null;
  return yearStart === "2000" ? 2000 + remainder : Number(yearStart) * 100 + remainder;
};

interface JoinedDictation {
  textBefore: string;
  transcript: string;
}

const finishUnfinishedYear = (textBefore: string, transcript: string): JoinedDictation | null => {
  const dateMatch = textBefore.match(UNFINISHED_YEAR_DATE_PATTERN);
  const remainderMatch = transcript.match(LEADING_YEAR_REMAINDER_PATTERN);
  if (!dateMatch || !remainderMatch) return null;

  const [whole, leadingSpace, month, day, yearStart] = dateMatch;
  if (!isMonth(Number(month)) || !isDay(Number(day))) return null;
  const year = completeYear(yearStart, remainderMatch[1]);
  if (year === null) return null;

  const dateStart = textBefore.length - whole.length + leadingSpace.length;
  return {
    textBefore: textBefore.slice(0, dateStart),
    transcript: `${month}/${day}/${year}${transcript.slice(remainderMatch[1].length)}`,
  };
};

export const MONTH_NAMES = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];
// "April 9, 2000" or "April 9 20" from a pause inside the year ("two thousand" then "twenty six").
const UNFINISHED_MONTH_NAME_YEAR_PATTERN = new RegExp(
  `(^|\\s)(${MONTH_NAMES.join("|")})(?:[ \\t]+(\\d{1,2}))?,?[ \\t]+(2000|19|20)[ \\t]*$`,
  "i"
);

const finishUnfinishedMonthNameYear = (
  textBefore: string,
  transcript: string
): JoinedDictation | null => {
  const dateMatch = textBefore.match(UNFINISHED_MONTH_NAME_YEAR_PATTERN);
  const remainderMatch = transcript.match(LEADING_YEAR_REMAINDER_PATTERN);
  if (!dateMatch || !remainderMatch) return null;

  const [whole, leadingSpace, month, day, yearStart] = dateMatch;
  // Without a day, "April 20" then "twenty six" could be the 20th, so only "April 2000" joins.
  if (day === undefined && yearStart !== "2000") return null;
  const year = completeYear(yearStart, remainderMatch[1]);
  if (year === null) return null;

  const dateStart = textBefore.length - whole.length + leadingSpace.length;
  const date = day === undefined ? `${month} ${year}` : `${month} ${day}, ${year}`;
  return {
    textBefore: textBefore.slice(0, dateStart),
    transcript: `${date}${transcript.slice(remainderMatch[1].length)}`,
  };
};

export interface JoinDictatedDateOptions {
  /** Also repairs month-name dates ("April 9, 2000" then "26"), which only plain mode writes. */
  includeMonthNameDates?: boolean;
}

// Deepgram can split one date across two final results ("done on nine /" then "fifteen / 2026.").
// Pulls a dangling date fragment from the text before into the new transcript to convert it whole.
export const joinDictatedDateAcrossChunks = (
  textBefore: string,
  transcript: string,
  { includeMonthNameDates = false }: JoinDictatedDateOptions = {}
): JoinedDictation => {
  const finishedYear =
    finishUnfinishedYear(textBefore, transcript) ??
    (includeMonthNameDates ? finishUnfinishedMonthNameYear(textBefore, transcript) : null);
  if (finishedYear) return finishedYear;

  const fragmentMatch = textBefore.match(TRAILING_DATE_FRAGMENT_PATTERN);
  if (fragmentMatch && LEADING_DATE_PART_PATTERN.test(transcript)) {
    const fragment = fragmentMatch[2];
    const joined = `${fragment.trimEnd()} ${transcript}`;
    const normalizedJoined = normalizeDictatedDates(joined);
    if (normalizedJoined !== joined) {
      return {
        textBefore: textBefore.slice(0, textBefore.length - fragment.length),
        transcript: normalizedJoined,
      };
    }
  }

  return { textBefore, transcript: normalizeDictatedDates(transcript, textBefore) };
};
